import { useCallback, useEffect, useState } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import { useNavigate } from "react-router-dom";
import apiClient from "../../../Utils/apiClient";
import { setLoggedIn, useMaterialUIController } from "context";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import socketService from "../../../socket/newsocketService";
import { adminAccessKey } from "../../../Redux/Slice/dataSlice";
import { useDispatch } from "react-redux";
import { setAccessKeyValue } from "context";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const [, dispatch] = useMaterialUIController();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const navigate = useNavigate();
  const dispatchh = useDispatch();

  const extractUsersList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.users)) return payload.users;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const clearStaleAdminSession = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
    localStorage.removeItem("state");
    localStorage.removeItem("superAdmin");
    localStorage.removeItem("isLoggedIn");
    setLoggedIn(dispatch, false);
    setAccessKeyValue(dispatch, false);
  }, [dispatch]);

  useEffect(() => {
    socketService.disconnect();
    clearStaleAdminSession();
  }, [clearStaleAdminSession]);

  const handleSignIn = useCallback(async () => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const trimmedPassword = String(pass || "").trim();

    console.log("[admin-login] submit", {
      email: normalizedEmail,
      hasPassword: Boolean(trimmedPassword),
    });

    if (!normalizedEmail || !trimmedPassword) {
      toast.warn("Email and password are required", {
        autoClose: 3000,
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }

    clearStaleAdminSession();

    try {
      console.log("[admin-login] request /admins/login", {
        email: normalizedEmail,
      });

      const response = await apiClient.post("/admins/login", {
        email: normalizedEmail,
        password: trimmedPassword,
      });

      console.log("[admin-login] raw /admins/login response", response);

      const userObject = response?.data || response?.admin || response; 

      if (!userObject) {
        toast.warn("Username or password do not match", {
          autoClose: 3000,
          position: toast.POSITION.TOP_CENTER,
        });
        return false;
      }

      const superAdminFlag =
        userObject?.superAdmin === true || userObject?.superAdmin === "true";
      const adminAccessApproved =
        userObject?.adminAccess === "Approved" || userObject?.adminAccess === true;
      const operatorAccess =
        userObject?.isOperator === true ||
        userObject?.role === "operator" ||
        userObject?.role === "admin" ||
        userObject?.useradmin === true;
      const hasAdminAccess = superAdminFlag || adminAccessApproved || operatorAccess;

      console.log("[admin-login] access check", {
        superAdminFlag,
        adminAccessApproved,
        operatorAccess,
        hasAdminAccess,
      });

      if (!hasAdminAccess) {
        const accessStatus = String(userObject?.adminAccess ?? "").trim().toLowerCase();
        let message = "Access denied: User does not have admin access";

        if (accessStatus === "pending") {
          message = "Your admin account is still pending approval.";
        } else if (accessStatus === "rejected") {
          message = "Your admin access request was rejected.";
        }

        toast.warn(message, {
          autoClose: 3000,
          position: toast.POSITION.TOP_CENTER,
        });
        return false;
      }

      const resolvedUserId =
        userObject?._id || userObject?.id || userObject?.userId || userObject?.key || null;
      const resolvedState = userObject?.state || userObject?.userState || "";

      if (!resolvedUserId) {
        toast.warn("Unable to sign in: missing user ID", {
          autoClose: 3000,
          position: toast.POSITION.TOP_CENTER,
        });
        return false;
      }

      setLoggedIn(dispatch, true);
      localStorage.setItem("user", JSON.stringify(userObject));
      localStorage.setItem("state", resolvedState);
      localStorage.setItem("superAdmin", String(superAdminFlag));
      localStorage.setItem("userId", String(resolvedUserId || ""));

      dispatchh(adminAccessKey(hasAdminAccess));
      setAccessKeyValue(dispatch, hasAdminAccess);

      socketService.disconnect();
      console.log("[admin-login] connecting socket", {
        userId: resolvedUserId,
        state: resolvedState,
        isSuperAdmin: superAdminFlag,
        web: "true",
      });
      void socketService
        .initializeSocket(resolvedUserId, resolvedState, {
          isSuperAdmin: superAdminFlag,
          web: "true",
        })
        .then(() => {
          console.log("[admin-login] socket connected");
        })
        .catch((socketError) => {
          console.log("[admin-login] socket connection failed", socketError);
        });

      navigate("/Online_Offline_Users");
      toast.success("Login successfully", {
        autoClose: 3000,
        position: toast.POSITION.TOP_CENTER,
      });
      return true;
    } catch (error) {
      console.log("[admin-login] sign in error", error);
      toast.warn(error?.message || "Unable to sign in", {
        autoClose: 3000,
        position: toast.POSITION.TOP_CENTER,
      });
      return false;
    }
  }, [pass, email, dispatch, dispatchh, navigate]);

  return (
    <BasicLayout>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={2}
          textAlign="center"
        >
          <ToastContainer />
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
          <Grid container spacing={0} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <MDTypography variant="h6" textAlign="center" fontWeight="medium" color="white" mt={1}>
              Karzame
            </MDTypography>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3} mt={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                onChange={(e) => setEmail(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                onChange={(e) => setPass(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              {/* <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography> */}
            </MDBox>
            <MDBox mt={5} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth onClick={() => handleSignIn()}>
                sign in
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
